// Meetings: start one now, or make a link to share for later; the links you host, with copy / open / end.
import { useState } from "react";
import { Link } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../lib/api";
import type { MeetingSummary } from "../../lib/meetings";
import { Alert, Badge, Button, Card, Dialog, Field, Select, Spinner } from "../../components/ui";
import { Icon } from "../../components/icons";
import { atLeast, errorText, useOrg, type Org } from "../org";

export function MeetingsPage() {
  const { org, role } = useOrg();
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<"instant" | null>(null);
  const [laterOpen, setLaterOpen] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const [scheduleFor, setScheduleFor] = useState<MeetingSummary | "new" | null>(null);
  const meetings = useQuery({ queryKey: ["meetings", org.slug], queryFn: () => api<{ meetings: MeetingSummary[] }>(`/orgs/${org.slug}/meetings`), refetchInterval: 15_000 });
  const canCreate = atLeast(role, "member");
  const all = meetings.data?.meetings ?? [];
  const upcoming = all.filter((m) => m.kind === "scheduled" && m.endsAt && new Date(m.endsAt).getTime() > Date.now()).sort((a, b) => a.startsAt!.localeCompare(b.startsAt!));
  const links = all.filter((m) => m.role === "host" && !upcoming.includes(m));

  async function startNow() {
    setBusy("instant"); setError(null);
    try {
      const r = await api<{ meeting: MeetingSummary }>(`/orgs/${org.slug}/meetings`, { method: "POST", body: { kind: "instant" } });
      window.location.assign(`/m/${r.meeting.code}`);
    } catch (err) { setError(errorText(err)); setBusy(null); }
  }
  async function copy(m: MeetingSummary) {
    try { await navigator.clipboard.writeText(m.url); setCopied(m.id); setTimeout(() => setCopied(null), 2000); } catch { window.prompt("Copy the meeting link:", m.url); }
  }
  async function end(m: MeetingSummary) {
    if (!confirm(`End "${m.title}"? The link will stop working${m.live ? " and the meeting in progress ends for everyone" : ""}.`)) return;
    try { await api(`/orgs/${org.slug}/meetings/${m.id}/end`, { method: "POST" }); await qc.invalidateQueries({ queryKey: ["meetings", org.slug] }); } catch (err) { setError(errorText(err)); }
  }

  return (
    <div className="mx-auto max-w-4xl">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div><h1 className="text-2xl font-semibold tracking-tight">Meetings</h1><p className="mt-1 text-sm text-slate-600 dark:text-slate-400">Share a link: people in {org.name} join directly, anyone else waits until someone lets them in.</p></div>
        {canCreate && (
          <div className="flex flex-wrap gap-2">
            <Link to={`/o/${org.slug}/recordings`}><Button variant="ghost"><Icon name="record" className="h-4 w-4" />Recordings</Button></Link>
            <Link to={`/o/${org.slug}/transcripts`}><Button variant="ghost"><Icon name="file" className="h-4 w-4" />Transcripts</Button></Link>
            <Button variant="secondary" onClick={() => setScheduleFor("new")}><Icon name="calendar" className="h-4 w-4" />Schedule</Button>
            <Button variant="secondary" onClick={() => setLaterOpen(true)}><Icon name="link" className="h-4 w-4" />Link for later</Button>
            <Button onClick={startNow} loading={busy === "instant"}><Icon name="video" className="h-4 w-4" />New meeting</Button>
          </div>
        )}
      </div>
      {error && <div className="mb-4"><Alert>{error}</Alert></div>}
      {upcoming.length > 0 && (
        <>
          <h2 className="mb-3 text-lg font-semibold">Upcoming</h2>
          <Card className="mb-8 p-0">
            <ul className="divide-y divide-slate-100 dark:divide-slate-800">
              {upcoming.map((m) => (
                <li key={m.id} className="flex flex-wrap items-center gap-3 px-5 py-4">
                  <span className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-brand-50 text-center leading-tight text-brand-700 dark:bg-brand-700/30 dark:text-brand-100">
                    <span><span className="block text-[10px] font-semibold uppercase">{new Date(m.startsAt!).toLocaleDateString([], { month: "short" })}</span><span className="block text-lg font-bold">{new Date(m.startsAt!).getDate()}</span></span>
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="flex flex-wrap items-center gap-2 font-medium"><span className="min-w-0 break-words">{m.title}</span>{m.live && <Badge tone="brand">Live · {m.live.people}</Badge>}{m.role === "invited" && <Badge>Invited</Badge>}</p>
                    <p className="text-sm text-slate-500">
                      {new Date(m.startsAt!).toLocaleString([], { weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })} – {new Date(m.endsAt!).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
                      {m.role === "invited" ? ` · from ${m.hostName ?? "someone"}` : m.invitees.length ? ` · ${m.invitees.length} invited` : ""}
                    </p>
                  </div>
                  <div className="flex w-full shrink-0 flex-wrap gap-1 sm:w-auto">
                    <Button size="sm" variant="ghost" onClick={() => copy(m)}><Icon name="link" className="h-4 w-4" />{copied === m.id ? "Copied" : "Copy"}</Button>
                    {m.role === "host" && <Button size="sm" variant="ghost" onClick={() => setScheduleFor(m)}>Edit</Button>}
                    <a href={`/m/${m.code}`}><Button size="sm" variant="secondary">Join</Button></a>
                    {m.role === "host" && <Button size="sm" variant="ghost" className="text-red-600" onClick={() => end(m)}>Cancel</Button>}
                  </div>
                </li>
              ))}
            </ul>
          </Card>
        </>
      )}
      {upcoming.length > 0 && <h2 className="mb-3 text-lg font-semibold">Meeting links</h2>}
      <Card className="p-0">
        {meetings.isLoading && <div className="flex justify-center p-8 text-brand-600"><Spinner /></div>}
        {meetings.data && links.length === 0 && (
          <div className="p-10 text-center">
            <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-2xl bg-brand-50 text-brand-600 dark:bg-brand-700/30"><Icon name="video" /></div>
            <p className="font-medium">{upcoming.length ? "No other meeting links" : "No meetings yet"}</p>
            <p className="mt-1 text-sm text-slate-500">Start a meeting now, schedule one, or create a link to share for later.</p>
          </div>
        )}
        <ul className="divide-y divide-slate-100 dark:divide-slate-800">
          {links.map((m) => (
            <li key={m.id} className="flex flex-wrap items-center gap-3 px-5 py-4">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-brand-50 text-brand-600 dark:bg-brand-700/30"><Icon name="video" /></span>
              <div className="min-w-0 flex-1">
                <p className="flex flex-wrap items-center gap-2 font-medium"><span className="min-w-0 break-words">{m.title}</span>{m.live && <Badge tone="brand">Live · {m.live.people}</Badge>}{m.lobby === "everyone" && <Badge>Everyone waits</Badge>}{!m.allowGuests && <Badge>Members only</Badge>}</p>
                <p className="truncate font-mono text-sm text-slate-500">{m.url.replace(/^https?:\/\//, "")}</p>
              </div>
              <div className="flex w-full shrink-0 flex-wrap gap-1 sm:w-auto">
                <Button size="sm" variant="ghost" onClick={() => copy(m)}><Icon name="link" className="h-4 w-4" />{copied === m.id ? "Copied" : "Copy"}</Button>
                <a href={`/m/${m.code}`}><Button size="sm" variant="secondary">{m.live ? "Join" : "Start"}</Button></a>
                <Button size="sm" variant="ghost" className="text-red-600" onClick={() => end(m)}>End</Button>
              </div>
            </li>
          ))}
        </ul>
      </Card>
      <LaterDialog open={laterOpen} onClose={() => setLaterOpen(false)} />
      <ScheduleDialog meeting={scheduleFor} onClose={() => setScheduleFor(null)} />
    </div>
  );
}

function LaterDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { org } = useOrg();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [made, setMade] = useState<MeetingSummary | null>(null);
  const close = () => { setMade(null); setError(null); onClose(); };
  async function create(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const data = new FormData(e.currentTarget);
    setBusy(true); setError(null);
    try {
      const r = await api<{ meeting: MeetingSummary }>(`/orgs/${org.slug}/meetings`, { method: "POST", body: {
        kind: "later", title: String(data.get("title") || "") || undefined, lobby: data.get("lobby"), allowGuests: data.get("guests") === "on",
      } });
      setMade(r.meeting);
      await qc.invalidateQueries({ queryKey: ["meetings", org.slug] });
    } catch (err) { setError(errorText(err)); } finally { setBusy(false); }
  }
  return (
    <Dialog open={open} onClose={close} title={made ? "Your meeting link" : "Create a link for later"}>
      {made ? (
        <div className="space-y-4">
          <p className="text-sm text-slate-600 dark:text-slate-400">Share it with the people you want to meet. It works for 30 days after its last use.</p>
          <div className="flex gap-2">
            <input readOnly value={made.url} aria-label="Meeting link" onFocus={(e) => e.currentTarget.select()} className="block min-w-0 flex-1 rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 font-mono text-sm dark:border-slate-700 dark:bg-slate-800" />
            <Button onClick={() => navigator.clipboard.writeText(made.url).catch(() => undefined)}>Copy</Button>
          </div>
          <div className="flex justify-end"><Button variant="secondary" onClick={close}>Done</Button></div>
        </div>
      ) : (
        <form onSubmit={create} className="space-y-4">
          {error && <Alert>{error}</Alert>}
          <Field label="Title" name="title" maxLength={120} placeholder="e.g. Project kick-off" />
          <MeetingOptions org={org} />
          <div className="flex justify-end gap-2"><Button type="button" variant="secondary" onClick={close}>Cancel</Button><Button type="submit" loading={busy}>Create link</Button></div>
        </form>
      )}
    </Dialog>
  );
}

const DURATIONS = [15, 30, 45, 60, 90, 120, 180, 240];
const zones = (): string[] => {
  const supported = (Intl as unknown as { supportedValuesOf?: (key: string) => string[] }).supportedValuesOf?.("timeZone");
  return supported?.length ? supported : ["UTC"];
};
const localParts = (iso: string, tz: string) => {
  const p = Object.fromEntries(new Intl.DateTimeFormat("en-US", { timeZone: tz, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }).formatToParts(new Date(iso)).map((x) => [x.type, x.value]));
  return { date: `${p.year}-${p.month}-${p.day}`, time: `${p.hour}:${p.minute}` };
};

// Schedule a new meeting, or change one (time, title) and invite more people. Times are entered in a chosen time
// zone (the browser's by default); invitations go by email with a calendar event.
function ScheduleDialog({ meeting, onClose }: { meeting: MeetingSummary | "new" | null; onClose: () => void }) {
  const { org } = useOrg();
  const qc = useQueryClient();
  const editing = meeting && meeting !== "new" ? meeting : null;
  const browserZone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [done, setDone] = useState<string | null>(null);
  const tz = editing?.timezone ?? browserZone;
  const initial = editing?.startsAt ? localParts(editing.startsAt, tz) : (() => { const d = new Date(Date.now() + 3600_000); d.setMinutes(0, 0, 0); return localParts(d.toISOString(), tz); })();
  const initialDuration = editing?.startsAt && editing.endsAt ? Math.round((new Date(editing.endsAt).getTime() - new Date(editing.startsAt).getTime()) / 60000) : 30;
  const close = () => { setError(null); setFields({}); setDone(null); onClose(); };

  async function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const data = new FormData(e.currentTarget);
    const emails = String(data.get("emails") ?? "").split(/[\s,;]+/).map((x) => x.trim()).filter(Boolean);
    const when = { start: `${data.get("date")}T${data.get("time")}`, timezone: String(data.get("timezone")), durationMinutes: Number(data.get("duration")) };
    setBusy(true); setError(null); setFields({});
    try {
      if (editing) {
        await api(`/orgs/${org.slug}/meetings/${editing.id}/schedule`, { method: "PATCH", body: { title: String(data.get("title")), ...when } });
        if (emails.length) await api(`/orgs/${org.slug}/meetings/${editing.id}/invite`, { method: "POST", body: { emails } });
        setDone(editing.invitees.length || emails.length ? "Saved. Everyone invited has been sent the updated time." : "Saved.");
      } else {
        await api(`/orgs/${org.slug}/meetings/schedule`, { method: "POST", body: { title: String(data.get("title")), ...when, emails, lobby: data.get("lobby"), allowGuests: data.get("guests") === "on" } });
        setDone(emails.length ? `Scheduled. ${emails.length === 1 ? "An invitation has" : `${emails.length} invitations have`} been emailed with a calendar event.` : "Scheduled. Copy the link from the list to share it.");
      }
      await qc.invalidateQueries({ queryKey: ["meetings", org.slug] });
    } catch (err) {
      setError(errorText(err));
      if (err && typeof err === "object" && "fields" in err) setFields((err as { fields: Record<string, string> }).fields);
    } finally { setBusy(false); }
  }

  return (
    <Dialog open={meeting !== null} onClose={close} title={editing ? "Edit meeting" : "Schedule a meeting"}>
      {done ? (
        <div className="space-y-4"><Alert kind="success">{done}</Alert><div className="flex justify-end"><Button onClick={close}>Done</Button></div></div>
      ) : (
        <form key={editing?.id ?? "new"} onSubmit={submit} className="space-y-4">
          {error && <Alert>{error}</Alert>}
          <Field label="Title" name="title" defaultValue={editing?.title ?? ""} maxLength={120} placeholder="e.g. Quarterly review" />
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Date" name="date" type="date" defaultValue={initial.date} required error={fields.start} />
            <Field label="Start time" name="time" type="time" defaultValue={initial.time} required />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <Select label="Length" name="duration" defaultValue={String(DURATIONS.includes(initialDuration) ? initialDuration : 30)}>
              {DURATIONS.map((d) => <option key={d} value={d}>{d < 60 ? `${d} minutes` : `${d / 60} hour${d > 60 ? "s" : ""}`}</option>)}
            </Select>
            <Select label="Time zone" name="timezone" defaultValue={tz}>
              {(zones().includes(tz) ? zones() : [tz, ...zones()]).map((z) => <option key={z} value={z}>{z.replace(/_/g, " ")}</option>)}
            </Select>
          </div>
          <div>
            <label htmlFor="schedule-emails" className="mb-1.5 block text-sm font-medium">{editing ? "Invite more people (optional)" : "Invite people (optional)"}</label>
            <textarea id="schedule-emails" name="emails" rows={2} placeholder="name@company.com, client@example.com" className="block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900" />
            <p className="mt-1.5 text-sm text-slate-500">{editing?.invitees.length ? `Already invited: ${editing.invitees.join(", ")}. ` : ""}Each person gets an email with the link and a calendar invitation.</p>
          </div>
          {!editing && (
            <>
              <MeetingOptions org={org} />
            </>
          )}
          <div className="flex justify-end gap-2"><Button type="button" variant="secondary" onClick={close}>Cancel</Button><Button type="submit" loading={busy}>{editing ? "Save" : "Schedule"}</Button></div>
        </form>
      )}
    </Dialog>
  );
}

// The waiting room and guests for a new meeting; the organization's settings can decide them for everyone.
function MeetingOptions({ org }: { org: Org }) {
  const everyoneWaits = Boolean(org.settings.lobbyForEveryone);
  const noGuests = org.settings.guestsAllowed === false;
  return (
    <>
      <Select label="Waiting room" name="lobby" defaultValue={everyoneWaits ? "everyone" : "guests"} disabled={everyoneWaits}>
        <option value="guests">People outside {org.name} wait to be let in</option>
        <option value="everyone">Everyone except me waits to be let in</option>
      </Select>
      {everyoneWaits && <p className="-mt-2 text-xs text-slate-500">{org.name}'s settings: everyone waits to be let in.</p>}
      <label className="flex items-start gap-3 text-sm">
        <input type="checkbox" name="guests" defaultChecked={!noGuests} disabled={noGuests} className="mt-0.5 h-4 w-4 rounded" />
        <span><span className="font-medium">Allow guests</span><br /><span className="text-slate-500">{noGuests ? `${org.name}'s settings don't allow guests in meetings.` : "People without an account (or from other organizations) can ask to join."}</span></span>
      </label>
    </>
  );
}
